import { useState } from 'react'
import { X, Copy, CheckCircle2, AlertCircle, Loader2, RefreshCw } from 'lucide-react'
import { api } from '../../services'
import { Organisation } from '../../services/api/types'

interface DomainVerificationModalProps {
  organisation: Organisation
  onClose: () => void
  onVerified: () => void
}

const DomainVerificationModal: React.FC<DomainVerificationModalProps> = ({
  organisation,
  onClose,
  onVerified
}) => {
  const [isVerifying, setIsVerifying] = useState(false)
  const [verificationError, setVerificationError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const orgId = organisation.organisation_id || organisation.id
  const verificationToken = 
    organisation.verification_token || 
    organisation.domain_verification?.verification_token
  const recordName = `_phisheez.${organisation.target_domain}`

  const handleCopyToken = async () => {
    if (verificationToken) {
      await navigator.clipboard.writeText(verificationToken)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleVerify = async () => {
    if (!orgId) return

    setIsVerifying(true)
    setVerificationError(null)

    try {
      await api.organisations.verifyDomain(orgId)
      onVerified()
    } catch (error: any) {
      // Show user-friendly error without HTTP codes
      setVerificationError(
        'Your domain is not yet verified. DNS propagation can take up to 24 hours. Please check that you\'ve added the correct TXT record and try again soon.'
      )
    } finally {
      setIsVerifying(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-lg shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Verify Domain: {organisation.target_domain}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {/* DNS Record Configuration */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Add TXT Record to DNS
            </h3>
            <div className="space-y-3">
              {/* Record Name */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name
                </label>
                <div className="flex items-center space-x-2">
                  <code className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-sm font-mono text-gray-900 dark:text-white">
                    {recordName}
                  </code>
                  <button
                    onClick={() => navigator.clipboard.writeText(recordName)}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    title="Copy"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Record Type */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type
                </label>
                <code className="block px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-sm font-mono text-gray-900 dark:text-white">
                  TXT
                </code>
              </div>

              {/* Record Value */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Value
                </label>
                {verificationToken ? (
                  <div className="flex items-center space-x-2">
                    <code className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-sm font-mono text-gray-900 dark:text-white break-all">
                      {verificationToken}
                    </code>
                    <button
                      onClick={handleCopyToken}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                      title="Copy"
                    >
                      {copied ? (
                        <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-sm text-gray-500 dark:text-gray-400">
                    Token not available. Please try refreshing the page.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Instructions
            </h3>
            <ol className="space-y-1 text-sm text-gray-700 dark:text-gray-300 list-decimal list-inside">
              <li>Log in to your DNS provider</li>
              <li>Add the TXT record above</li>
              <li>Wait 5-30 minutes for DNS propagation</li>
              <li>Click "Verify Domain" below</li>
            </ol>
          </div>

          {/* DNS Provider Links */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              DNS Provider Guides
            </h3>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <a
                href="https://docs.aws.amazon.com/Route53/latest/DeveloperGuide/resource-record-sets-creating.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                AWS Route 53 →
              </a>
              <a
                href="https://learn.microsoft.com/en-us/azure/dns/dns-operations-recordsets-portal"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                Azure DNS →
              </a>
              <a
                href="https://support.google.com/domains/answer/3290350"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                Google Domains →
              </a>
              <a
                href="https://www.cloudflare.com/learning/dns/dns-records/dns-txt-record/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                Cloudflare →
              </a>
            </div>
          </div>

          {/* Error Message */}
          {verificationError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-900 dark:text-red-100">
                  {verificationError}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            Close
          </button>
          <button
            onClick={handleVerify}
            disabled={isVerifying || !verificationToken}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white text-sm font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isVerifying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Verify Domain
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DomainVerificationModal
